import { Injectable, inject } from '@angular/core';
import { TaskService } from './task.service';
import { Message } from '../models/chat.model';

/**
 * Synchronisation des messages de type tâche/statut avec le `TaskService`
 * local — extrait de `ChatService` (qui dépassait 500 lignes, Règle 12).
 *
 * Ne touche aucun signal partagé de `ChatService` (`_conversations`,
 * `_messages`) : reçoit les messages déjà chargés en paramètre, ne dépend
 * que de `TaskService` et de son propre état privé (`syncedTaskMessageIds`)
 * — frontière de dépendance nette, pas un découpage arbitraire.
 */
@Injectable({ providedIn: 'root' })
export class ChatTaskSyncService {
    private taskService = inject(TaskService);
    private syncedTaskMessageIds = new Set<string>();

    /** Ajoute les messages-tâches des autres au TaskService local, puis synchronise les statuts. */
    syncIncomingTasks(messages: Message[]): void {
        for (const msg of messages) {
            if (msg.type !== 'task' || msg.sender === 'me') continue;
            if (this.syncedTaskMessageIds.has(msg.id)) continue;

            // Check if already in task list (by matching text + approximate timestamp)
            const existing = this.taskService.todos().find(t =>
                t.text === (msg.taskData?.text || msg.text) &&
                Math.abs(t.createdAt - msg.timestamp) < 60_000 // within 1 minute
            );

            if (!existing) {
                const taskText = msg.taskData?.text || msg.text.replace(/^✅\s*(Tâche assignée:\s*)?/, '').trim();
                this.taskService.addTodo(
                    taskText,
                    msg.senderName || 'manager',
                    undefined,
                    'medium',
                    undefined,
                    msg.conversationId
                );
            }

            this.syncedTaskMessageIds.add(msg.id);
        }

        // Sync task status updates: look for "task_finished" system messages from the other side
        this.syncTaskStatusUpdates(messages);
    }

    /** Sync status updates: when the receiver marks a task "finished", update sender's local task */
    private syncTaskStatusUpdates(messages: Message[]): void {
        for (const msg of messages) {
            if (msg.type !== 'system' || msg.sender === 'me') continue;
            if (this.syncedTaskMessageIds.has(msg.id)) continue;

            // Pattern: "TASK_FINISHED:taskText" — the receiver finished the task
            if (msg.text.startsWith('TASK_FINISHED:')) {
                const taskText = msg.text.substring('TASK_FINISHED:'.length).trim();
                const todo = this.taskService.todos().find(t =>
                    t.text === taskText && t.creatorId === 'me' && t.status === 'pending'
                );
                if (todo) {
                    this.taskService.finishTodo(todo.id);
                }
            }

            this.syncedTaskMessageIds.add(msg.id);
        }
    }
}
